import React, { JSX } from 'react';
import { Button } from './shared/ui/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader } from './shared/ui/Dialog';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  busy?: boolean;
  danger?: boolean;
  showCancel?: boolean;
  stackedAboveRacePortal?: boolean;
  /** See Dialog's overlayZIndex — needed when nested inside an overlay above z-5200 (e.g. DeviceRosterOverlay at 5300). */
  overlayZIndex?: number;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
};

export default function ConfirmDialog(props: ConfirmDialogProps): JSX.Element | null {
  const {
    open,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    busy = false,
    danger = true,
    showCancel = true,
    stackedAboveRacePortal = false,
    overlayZIndex,
    onConfirm,
    onCancel,
  } = props;

  const headerTone = danger
    ? 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30'
    : 'border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/30';
  const titleClass = danger
    ? 'text-red-900 dark:text-red-200'
    : 'text-blue-900 dark:text-blue-200';
  const bodyClass = danger
    ? 'text-red-950 dark:text-red-100'
    : 'text-blue-950 dark:text-blue-100';

  return (
    <Dialog
      open={open}
      panelClassName="max-w-xl"
      stackedAboveRacePortal={stackedAboveRacePortal}
      overlayZIndex={overlayZIndex}
    >
      <DialogHeader className={headerTone}>
        <div className={`text-sm font-semibold ${titleClass}`}>{title}</div>
      </DialogHeader>
      <DialogBody className={`space-y-3 ${bodyClass}`}>{message}</DialogBody>
      <DialogFooter>
        {showCancel ? (
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelText}
          </Button>
        ) : null}
        <Button
          variant={danger ? 'danger' : 'primary'}
          loading={busy}
          onClick={() => void onConfirm()}
          className="min-w-[5.5rem]"
        >
          {confirmText}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
